<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { authFetch, getToken } from '../authService';
  import { t } from '../i18nStore';

  // --- STATE ---
  let selectedFile = null;
  let urlInput = '';
  let isLoading = false;
  let successMessage = '';
  let errorMessage = '';
  
  // Existing Cards
  let allCreditCards = [];
  let isLoadingList = true;
  
  // Staging Area (Extracted but not saved)
  let extractedCards = []; 

  // Edit State
  let editingCardId = null;
  let editingCardData = {}; 

  // Manual Entry State
  let manualName = '';
  let manualBank = '';
  let manualIssuer = 'Visa';
  let manualCountry = $selectedCountry || 'HK';
  let manualActive = true;
  let manualShortTag = '';
  let manualAliasesStr = '';

  onMount(() => {
    if ($selectedCountry) manualCountry = $selectedCountry;
    fetchCreditCards();
  });

  async function fetchCreditCards() {
    isLoadingList = true;
    try {
      const response = await authFetch(getApiUrl('/admin/credit-cards/list'));
      if (!response.ok) throw new Error('Could not fetch credit cards.');
      allCreditCards = await response.json();
    } catch (err) {
      errorMessage = err.message;
    } finally {
      isLoadingList = false;
    }
  }

  // --- MANUAL ADD ---
  async function handleManualAdd() {
    if (!manualName || !manualBank || !manualCountry) {
        errorMessage = "Name, Bank, and Country are required.";
        return;
    }

    isLoading = true;
    errorMessage = '';
    successMessage = '';

    const payload = {
        name: manualName,
        bankName: manualBank,
        issuer: manualIssuer,
        countryCode: manualCountry,
        isActive: manualActive,
        shortTag: manualShortTag.trim() || undefined,
        aliases: manualAliasesStr.split(',').map(s => s.trim()).filter(Boolean)
    };

    try {
        const res = await authFetch(getApiUrl('/admin/credit-cards'), {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || "Failed to add card");
        }

        successMessage = $t('admin.credit_cards.save_success');
        // Reset form
        manualName = '';
        manualBank = '';
        manualIssuer = 'Visa';
        manualShortTag = '';
        manualAliasesStr = '';
        fetchCreditCards();

    } catch(e) {
        errorMessage = e.message;
    } finally {
        isLoading = false;
    }
  }

  // --- AI EXTRACTION ---
  async function handleExtract() {
      if (!urlInput) return;
      isLoading = true;
      errorMessage = '';
      successMessage = '';
      
      try {
          const res = await authFetch(getApiUrl('/credit-cards/extract'), {
              method: 'POST',
              body: JSON.stringify({ url: urlInput })
          });
          
          if (!res.ok) throw new Error("Failed to extract data");
          
          const data = await res.json();
          extractedCards = data.map(c => ({
              ...c,
              countryCode: $selectedCountry || 'HK',
              isActive: true,
              aliases: []
          }));
          
          successMessage = `Found ${extractedCards.length} cards! Review below before saving.`;
      } catch(e) {
          errorMessage = e.message;
      } finally {
          isLoading = false;
      }
  }

  // --- SAVING EXTRACTED CARDS ---
  async function saveExtractedCard(index) {
      const card = extractedCards[index];
      try {
          const res = await authFetch(getApiUrl('/admin/credit-cards'), {
              method: 'POST',
              body: JSON.stringify(card)
          });
          
          if (res.ok) {
              extractedCards = extractedCards.filter((_, i) => i !== index);
              fetchCreditCards();
          } else {
              alert("Failed to save. Might be duplicate.");
          }
      } catch(e) {
          alert(e.message);
      }
  }

  function removeExtracted(index) {
      extractedCards = extractedCards.filter((_, i) => i !== index);
  }

  // --- EDITING EXISTING CARDS ---
  function startEdit(card) {
      editingCardId = card._id;
      editingCardData = { 
          ...card,
          shortTag: card.shortTag || '',
          aliasesStr: (card.aliases || []).join(', ')
      };
  }

  function cancelEdit() {
      editingCardId = null;
      editingCardData = {};
  }

  async function saveEdit() {
      editingCardData.aliases = (editingCardData.aliasesStr || '').split(',').map(s => s.trim()).filter(Boolean);
      const { aliasesStr, ...payload } = editingCardData;
      try {
          const res = await authFetch(getApiUrl(`/admin/credit-cards/${editingCardId}`), {
              method: 'PUT',
              body: JSON.stringify(payload)
          });
          if (res.ok) {
              cancelEdit();
              fetchCreditCards();
          } else {
              const d = await res.json();
              alert(d.error);
          }
      } catch(e) {
          alert("Update failed");
      }
  }

  async function deleteCard(id) {
      if(!confirm($t('admin.credit_cards.delete_confirm'))) return;
      try {
          await authFetch(getApiUrl(`/admin/credit-cards/${id}`), { method: 'DELETE' });
          fetchCreditCards();
      } catch(e) { alert("Delete failed"); }
  }

  // --- BULK UPLOAD ---
  function handleFileSelect(e) { selectedFile = e.target.files[0]; }
  
  async function handleUpload() {
    if (!selectedFile) return;
    isLoading = true;
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const token = await getToken();
      const response = await fetch(getApiUrl('/credit-cards/upload'), { 
          method: 'POST', 
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData 
      });
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      successMessage = `Upload successful! ${result.processed} records processed.`;
      selectedFile = null;
      document.getElementById('file-input').value = '';
      fetchCreditCards();
    } catch (err) { errorMessage = err.message; } finally { isLoading = false; }
  }
</script>

<div class="maintenance-container">
  <h2>{$t('admin.credit_cards.title')}</h2>

  {#if successMessage}<p class="success">{successMessage}</p>{/if}
  {#if errorMessage}<p class="error">{errorMessage}</p>{/if}

  <!-- SECTION 1: AI IMPORT -->
  <div class="card section">
      <h3>{$t('admin.credit_cards.option_ai')}</h3>
      <div class="url-input-group">
          <input type="text" bind:value={urlInput} placeholder={$t('admin.credit_cards.url_placeholder')} />
          <button class="primary" on:click={handleExtract} disabled={isLoading}>
            {isLoading ? $t('admin.credit_cards.extracting_btn') : $t('admin.credit_cards.extract_btn')}
          </button>
      </div>
      
      {#if extractedCards.length > 0}
        <div class="staging-area">
            <h4>{$t('admin.credit_cards.staging_title').replace('{count}', extractedCards.length.toString())}</h4>
            <table>
                <thead>
                    <tr>
                        <th>{$t('admin.credit_cards.bank_name')}</th>
                        <th>{$t('admin.credit_cards.card_name')}</th>
                        <th>{$t('admin.credit_cards.issuer')}</th>
                        <th>{$t('admin.credit_cards.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each extractedCards as card, i}
                        <tr>
                            <td><input type="text" bind:value={card.bankName} /></td>
                            <td><input type="text" bind:value={card.name} /></td>
                            <td>
                                <select bind:value={card.issuer}>
                                    <option value="Visa">Visa</option>
                                    <option value="Mastercard">Mastercard</option>
                                    <option value="American Express (Amex)">Amex</option>
                                    <option value="UnionPay">UnionPay</option>
                                    <option value="JCB (Japan Credit Bureau)">JCB</option>
                                    <option value="Diners Club International">Diners</option>
                                    <option value="Discover">Discover</option>
                                </select>
                            </td>
                            <td>
                                <div class="btn-group">
                                    <button class="success-btn" on:click={() => saveExtractedCard(i)}>Save</button>
                                    <button class="delete-btn" on:click={() => removeExtracted(i)}>X</button>
                                </div>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
      {/if}
  </div>

  <!-- SECTION 2: BULK CSV UPLOAD -->
  <div class="card section">
      <h3>{$t('admin.credit_cards.option_csv')}</h3>
      <div class="row flex-row">
          <input type="file" id="file-input" accept=".csv" on:change={handleFileSelect}>
          <button class="secondary" on:click={handleUpload} disabled={isLoading || !selectedFile}>{$t('admin.credit_cards.upload_csv_btn')}</button>
      </div>
  </div>

  <!-- SECTION 3: MANUAL ENTRY -->
  <div class="card section">
      <h3>{$t('admin.credit_cards.option_manual')}</h3>
      <div class="manual-form">
          <div class="form-group">
              <label>{$t('admin.credit_cards.bank_name')}</label>
              <input type="text" bind:value={manualBank} placeholder="e.g. HSBC" />
          </div>
          <div class="form-group">
              <label>{$t('admin.credit_cards.card_name')}</label>
              <input type="text" bind:value={manualName} placeholder="e.g. Red Credit Card" />
          </div>
          <div class="form-group">
              <label>{$t('admin.credit_cards.short_tag')}</label>
              <input type="text" bind:value={manualShortTag} placeholder="e.g. DBSBlackAmex" />
          </div>
          <div class="form-group">
              <label>{$t('admin.credit_cards.aliases_label')}</label>
              <input type="text" bind:value={manualAliasesStr} placeholder={$t('admin.credit_cards.aliases')} />
          </div>
          <div class="form-group">
              <label>{$t('admin.credit_cards.issuer')}</label>
              <select bind:value={manualIssuer}>
                  <option value="Visa">Visa</option>
                  <option value="Mastercard">Mastercard</option>
                  <option value="American Express">American Express</option>
                  <option value="UnionPay">UnionPay</option>
                  <option value="JCB (Japan Credit Bureau)">JCB</option>
                  <option value="Diners Club International">Diners</option>
                  <option value="Discover">Discover</option>
              </select>
          </div>
          <div class="form-group">
              <label>{$t('admin.credit_cards.country')}</label>
              <input type="text" bind:value={manualCountry} placeholder="HK" />
          </div>
          <div class="form-group checkbox">
              <label>
                  <input type="checkbox" bind:checked={manualActive}> {$t('admin.credit_cards.active')}
              </label>
          </div>
          <button class="primary" on:click={handleManualAdd} disabled={isLoading}>{$t('admin.credit_cards.add_card')}</button>
      </div>
  </div>

  <!-- SECTION 4: EXISTING LIST -->
  <div class="card list-section">
    <h3>{$t('admin.credit_cards.all_cards')}</h3>
    {#if isLoadingList}
      <p>{$t('admin.credit_cards.loading')}</p>
    {:else}
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>{$t('admin.credit_cards.country')}</th>
              <th>{$t('admin.credit_cards.bank_name')}</th>
              <th>{$t('admin.credit_cards.card_name')}</th>
              <th>{$t('admin.credit_cards.short_tag')}</th>
              <th>{$t('admin.credit_cards.aliases_label')}</th>
              <th>{$t('admin.credit_cards.issuer')}</th>
              <th>{$t('admin.credit_cards.active')}</th>
              <th>{$t('admin.credit_cards.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {#each allCreditCards as card (card._id)}
              <tr>
                {#if editingCardId === card._id}
                    <!-- EDIT MODE -->
                    <td data-label="Country"><input type="text" bind:value={editingCardData.countryCode} style="width: 50px" /></td>
                    <td data-label="Bank"><input type="text" bind:value={editingCardData.bankName} /></td>
                    <td data-label="Name"><input type="text" bind:value={editingCardData.name} /></td>
                    <td data-label="Short Tag"><input type="text" bind:value={editingCardData.shortTag} placeholder="e.g. DBSBlackAmex" /></td>
                    <td data-label="Aliases"><input type="text" bind:value={editingCardData.aliasesStr} /></td>
                    <td data-label="Issuer">
                        <select bind:value={editingCardData.issuer}>
                            <option value="Visa">Visa</option>
                            <option value="Mastercard">Mastercard</option>
                            <option value="American Express (Amex)">Amex</option>
                            <option value="UnionPay">UnionPay</option>
                            <option value="JCB (Japan Credit Bureau)">JCB</option>
                            <option value="Diners Club International">Diners</option>
                            <option value="Discover">Discover</option>
                        </select>
                    </td>
                    <td data-label="Active"><input type="checkbox" bind:checked={editingCardData.isActive} /></td>
                    <td data-label="Actions">
                        <div class="btn-group">
                            <button class="success-btn" on:click={saveEdit}>💾</button>
                            <button class="secondary" on:click={cancelEdit}>🚫</button>
                        </div>
                    </td>
                {:else}
                    <!-- VIEW MODE -->
                    <td data-label="Country">{card.countryCode}</td>
                    <td data-label="Bank">{card.bankName}</td>
                    <td data-label="Name">{card.name}</td>
                    <td data-label="Short Tag">{card.shortTag || '-'}</td>
                    <td data-label="Aliases">{(card.aliases || []).join(', ')}</td>
                    <td data-label="Issuer">{card.issuer}</td>
                    <td data-label="Active">{card.isActive ? '✅' : '❌'}</td>
                    <td data-label="Actions">
                        <div class="btn-group">
                            <button class="edit-btn" on:click={() => startEdit(card)}>✏️</button>
                            <button class="delete-btn" on:click={() => deleteCard(card._id)}>🗑️</button>
                        </div>
                    </td>
                {/if}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</div>

<style>
  .maintenance-container { max-width: 1000px; margin: 2rem auto; padding: 0 1rem; }
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); margin-bottom: 2rem; }
  
  .url-input-group { display: flex; gap: 1rem; flex-wrap: wrap; }
  .url-input-group input { flex-grow: 1; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; min-width: 200px; }
  
  /* Manual Form */
  .manual-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem; align-items: end; }
  .form-group { display: flex; flex-direction: column; }
  .form-group label { font-size: 0.85rem; font-weight: bold; margin-bottom: 0.3rem; }
  .form-group.checkbox { flex-direction: row; align-items: center; padding-bottom: 0.8rem;}
  
  .table-wrapper { max-height: 500px; overflow-y: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  th, td { padding: 8px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
  thead { position: sticky; top: 0; background: #f8f9fa; z-index: 1; }
  
  input[type="text"], select { width: 100%; padding: 0.4rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  
  button { padding: 0.5rem 1rem; border: none; border-radius: 4px; cursor: pointer; }
  .primary { background-color: #007bff; color: white; }
  .secondary { background-color: #6c757d; color: white; }
  .success-btn { background-color: #28a745; color: white; padding: 4px 8px; }
  .edit-btn { background-color: #ffc107; color: #333; padding: 4px 8px; }
  .delete-btn { background-color: #dc3545; color: white; padding: 4px 8px; }
  
  .btn-group { display: flex; gap: 5px; }

  .staging-area { margin-top: 1.5rem; border-top: 2px solid #eee; padding-top: 1rem; }
  .staging-area h4 { margin-top: 0; color: #28a745; }
  
  .success { color: #28a745; font-weight: bold; }
  .error { color: #dc3545; font-weight: bold; }

  /* --- MOBILE RESPONSIVE TABLE --- */
  @media (max-width: 768px) {
    thead { display: none; }
    table, tbody, tr, td { display: block; width: 100%; }
    tr { margin-bottom: 1rem; border: 1px solid #ddd; border-radius: 8px; padding: 1rem; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
    td { padding: 0.5rem 0; display: flex; justify-content: space-between; align-items: center; border: none; border-bottom: 1px solid #f1f1f1; }
    td:last-child { border-bottom: none; margin-top: 0.5rem; justify-content: flex-end; }
    td::before { content: attr(data-label); font-weight: bold; margin-right: 1rem; color: #555; min-width: 60px; }
    td input[type="text"], td select { width: 60%; text-align: right; }
    
    .manual-form { grid-template-columns: 1fr; }
  }
</style>
